import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import FormSelect from "../components/FormSelect";
import PrimaryButton from "../components/PrimaryButton";

const AUTH_TOKEN_STORAGE_KEY = "authToken";
const AUTH_ROLE_STORAGE_KEY = "authRole";
const TANNERY_DEFAULT_EID_DAY_STORAGE_KEY = "tanneryDefaultEidDay";
const GLOBAL_DEFAULT_EID_DAY_STORAGE_KEY = "globalDefaultEidDay";
const EID_DAY_OPTIONS = ["Eid Day 1", "Eid Day 2", "Eid Day 3", "Eid Day 4"];

const extractEidDayFromSettingsPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return "";
  }
  const raw = payload.data?.eid_day ?? payload.eid_day ?? payload.default_eid_day ?? "";
  return String(raw ?? "").trim();
};

const normalizeRole = (role) => {
  const cleaned = String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

  if (cleaned === "TANNERYADMIN") {
    return "TANNERY_ADMIN";
  }
  if (cleaned === "SUPERADMIN") {
    return "SUPER_ADMIN";
  }

  return cleaned;
};

const getBaseApiUrl = () => {
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("REACT_APP_BASE_API_URL is not configured.");
  }
  return baseApiUrl.replace(/\/+$/, "");
};

const parseApiMessage = async (response, fallbackMessage) => {
  const responseText = await response.text();
  if (!responseText) {
    return fallbackMessage;
  }

  try {
    const payload = JSON.parse(responseText);
    if (typeof payload?.detail === "string" && payload.detail.trim()) {
      return payload.detail;
    }
    return payload?.message || fallbackMessage;
  } catch (parseError) {
    return responseText;
  }
};

function SettingsPage() {
  const navigate = useNavigate();
  const userRole = normalizeRole(localStorage.getItem(AUTH_ROLE_STORAGE_KEY));
  const isSuperAdmin = userRole === "SUPER_ADMIN";
  const isTanneryAdmin = userRole === "TANNERY_ADMIN";
  const canAccessSettings = isSuperAdmin || isTanneryAdmin;

  const settingsEidDayPath = useMemo(
    () =>
      isSuperAdmin ? "/super-admin/settings/eid-day" : "/tannery-admin/settings/eid-day",
    [isSuperAdmin]
  );

  const [selectedEidDay, setSelectedEidDay] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!canAccessSettings) {
      return undefined;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      return undefined;
    }

    let cancelled = false;
    const loadCurrentEidDay = async () => {
      try {
        setIsLoading(true);
        setApiError("");
        const response = await fetch(`${getBaseApiUrl()}${settingsEidDayPath}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        const responseText = await response.text();
        if (cancelled) {
          return;
        }

        const storageKey = isSuperAdmin
          ? GLOBAL_DEFAULT_EID_DAY_STORAGE_KEY
          : TANNERY_DEFAULT_EID_DAY_STORAGE_KEY;

        if (!response.ok) {
          if (!isSuperAdmin && response.status === 405) {
            const cached = localStorage.getItem(storageKey);
            if (cached?.trim()) {
              setSelectedEidDay(cached.trim());
            }
            return;
          }
          throw new Error(
            await parseApiMessage(response, "Unable to load settings right now.")
          );
        }

        if (!responseText) {
          return;
        }

        let payload;
        try {
          payload = JSON.parse(responseText);
        } catch {
          return;
        }

        const day = extractEidDayFromSettingsPayload(payload);
        if (day) {
          setSelectedEidDay(day);
          localStorage.setItem(storageKey, day);
        }
      } catch (error) {
        if (!cancelled) {
          setApiError(error.message || "Unable to load settings right now.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadCurrentEidDay();
    return () => {
      cancelled = true;
    };
  }, [canAccessSettings, isSuperAdmin, settingsEidDayPath]);

  const handleSaveSettings = async (event) => {
    event.preventDefault();

    if (!selectedEidDay.trim()) {
      setFieldError("Please select an Eid day.");
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      setApiError("Session expired. Please login again.");
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsSaving(true);
      setFieldError("");
      setApiError("");
      setSuccessMessage("");

      const response = await fetch(`${getBaseApiUrl()}${settingsEidDayPath}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ eid_day: selectedEidDay }),
      });

      if (!response.ok) {
        throw new Error(
          await parseApiMessage(response, "Unable to save settings right now.")
        );
      }

      const responseText = await response.text();
      let savedDay = selectedEidDay;
      if (responseText) {
        try {
          const payload = JSON.parse(responseText);
          savedDay = extractEidDayFromSettingsPayload(payload) || selectedEidDay;
        } catch {
          /* use selectedEidDay */
        }
      }

      const storageKey = isSuperAdmin
        ? GLOBAL_DEFAULT_EID_DAY_STORAGE_KEY
        : TANNERY_DEFAULT_EID_DAY_STORAGE_KEY;
      localStorage.setItem(storageKey, savedDay);

      setSuccessMessage(
        isSuperAdmin
          ? `Global default Eid day saved as ${savedDay}.`
          : `Tannery default Eid day saved as ${savedDay}.`
      );
    } catch (error) {
      setApiError(error.message || "Unable to save settings right now.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectEidDay = (event) => {
    setSelectedEidDay(event.target.value);
    setFieldError("");
    setApiError("");
    setSuccessMessage("");
  };

  if (!canAccessSettings) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <section className="dashboard-card" id="settings">
      <h2>Settings</h2>
      <p className="dashboard-card-subtitle">
        {isSuperAdmin
          ? "Set the global default Eid day (used when generating tokens and in audit logs). Each tannery can still override this with its own default."
          : "Select and save the default Eid day for your tannery (overrides the global default)."}
      </p>

      {successMessage ? (
        <p className="dashboard-success-message" role="status">
          {successMessage}
        </p>
      ) : null}
      {apiError ? (
        <p className="dashboard-error-message" role="alert">
          {apiError}
        </p>
      ) : null}

      <form onSubmit={handleSaveSettings} noValidate>
        <FormSelect
          id="eidDay"
          name="eidDay"
          label={isSuperAdmin ? "Global default Eid Day" : "Default Eid Day"}
          value={selectedEidDay}
          onChange={handleSelectEidDay}
          options={EID_DAY_OPTIONS}
          placeholder={isLoading ? "Loading..." : "Select Eid day"}
          error={fieldError}
          required
          disabled={isLoading || isSaving}
        />

        <PrimaryButton type="submit" disabled={isLoading || isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </PrimaryButton>
      </form>
    </section>
  );
}

export default SettingsPage;
